import * as yup from 'yup';
import { formatDateToString } from '../../helpers';
import { REMEDY_PRICE } from '../../constants';

export const Schema = (organisation, whiteLabels) => {
  const {
    name: orgName,
    marketing_name: marketingName,
    notes: orgNotes,
    address: { name, street, postcode, city } = {},
    primaryUser: { firstName, lastName, position, email, telephone } = {},
    discount,
    vat_number,
    currentContract: {
      unitPrice,
      slots,
      startDate,
      endDate,
      count,
      trialPeriod,
      paymentStartDate,
      contractSignedDate,
    } = {},
    white_label_app_id,
    membership_id,
    commissions: {
      defaultRate: {
        transaction_fee: transaction,
        transaction_fee_percentage: transactionPercent,
        delivery,
        collect,
        otc_commission,
        minimum_charge_threshold,
        non_referred_item,
        free_items_count,
        site_cap,
        referred_appointments: referredAppointmentsFee,
        text_alerts: textAlertsFee,
        non_referred: localConsultationFee,
        referred_consultation: referredConsultationFee,
        appointment_commission_rate_percentage,
      } = {}
    } = {},
    paid_delivery: {
      delivery_cost,
      delivery_fee,
      concession_cost,
      concession_fee
    } = {}
  } = organisation;

  const whiteLabelValues = [
    'none',
    ...whiteLabels.map(i => i.name),
  ];
  const memberValues = [
    'none',
    ...whiteLabels.filter(i => i.members).map(i => i.name)
  ]
  const { name: selectedWhiteLabelValue } = whiteLabels.find(i => i._id === white_label_app_id) || {};
  const { name: selectedMemberLabelValue } = whiteLabels.find(i => i._id === membership_id) || {};

  return {
    address: {
      'Name': { defaultValue: orgName, field: 'name', fieldName: 'name', validation: yup.string().required() },
      'Marketing Name': { defaultValue: marketingName, field: 'marketing_name', fieldName: 'marketing_name', validation: yup.string().required() },
      'Address Line 1': { defaultValue: name, field: 'address.name', fieldName: 'name', validation: yup.string().required() },
      'Address Line 2': { defaultValue: street, field: 'address.street', fieldName: 'street', validation: yup.string().required() },
      'Postcode': { defaultValue: postcode, field: 'address.postcode', fieldName: 'postcode', validation: yup.string().required() },
      'City': { defaultValue: city, field: 'address.city', fieldName: 'city', validation: yup.string().required() },
      'VAT Number': { defaultValue: vat_number, field: 'vat_number', fieldName: 'vat_number', validation: yup.string() },
    },
    orgNotes: {
      'Notes': { defaultValue: orgNotes, field: 'notes', multiline: true, fieldName: 'notes', validation: yup.string().required() },
    },
    primaryUser: {
      'First Name': { defaultValue: firstName, field: 'primaryUser.firstName', fieldName: 'firstName', validation: yup.string().required() },
      'Last Name': { defaultValue: lastName, field: 'primaryUser.lastName', fieldName: 'lastName', validation: yup.string().required() },
      'Position': { defaultValue: position, field: 'primaryUser.position', fieldName: 'position', validation: yup.string().required() },
      'Email': { defaultValue: email, field: 'primaryUser.email', fieldName: 'email', validation: yup.string().required() },
      'Telephone': { defaultValue: telephone, field: 'primaryUser.telephone', fieldName: 'telephone', validation: yup.string().required() },
      'Slots': { defaultValue: slots, field: 'currentContract.slots', fieldName: 'slots', validation: yup.string().required() },
      'White Label': {
        type: 'editableSelect',
        values: whiteLabelValues,
        defaultValue: selectedWhiteLabelValue,
        field: 'white_label_app_id',
        validation: yup.string().required(),
      },
      'Membership': {
        type: 'editableSelect',
        values: memberValues,
        defaultValue: selectedMemberLabelValue,
        field: 'membership_id',
        validation: yup.string().required(),
      },
    },
    commissionRatesAndFees: {
      'Price (Per Month Per Site)': {
        defaultValue: unitPrice,
        field: 'currentContract.unitPrice',
        fieldName: 'unitPrice',
        validation: yup.string().required(),
      },
      'Number of Pharmacy Sites': {
        defaultValue: slots,
        field: 'currentContract.slots',
        fieldName: 'slots',
        validation: yup.string().required(),
      },
      'Commission rate - Delivery': {
        defaultValue: delivery,
        type: 'number',
        field: 'commissions.defaultRate.delivery',
        validation: yup.number().min(0).max(REMEDY_PRICE).required()
      },
      'Commission rate - Collection': {
        defaultValue: collect,
        type: 'number',
        field: 'commissions.defaultRate.collect',
        validation: yup.number().min(0).max(REMEDY_PRICE).required()
      },
      'Commission rate - Non-Referred': {
        defaultValue: non_referred_item,
        type: 'number',
        field: 'commissions.defaultRate.non_referred_item',
        validation: yup.number().min(0).max(REMEDY_PRICE),
        cast: castEmptyStringToNull
      },
      'Minimum Charge Threshold': {
        defaultValue: minimum_charge_threshold,
        type: 'number',
        field: 'commissions.defaultRate.minimum_charge_threshold',
        validation: yup.number().min(0),
        cast: castEmptyStringToNull
      },
      'Free Items': {
        defaultValue: free_items_count,
        type: 'number',
        field: 'commissions.defaultRate.free_items_count',
        validation: yup.number().min(0),
        cast: castEmptyStringToNull
      },
      'Site Cap': {
        defaultValue: site_cap,
        type: 'number',
        field: 'commissions.defaultRate.site_cap',
        validation: yup.number().nullable(true).min(0),
        cast: castEmptyStringToNull
      },
      'Commission rate - E-Commerce - %': {
        defaultValue: otc_commission,
        type: 'number',
        field: 'commissions.defaultRate.otc_commission',
        validation: yup.number().min(0).max(100).required(),
      },
      'Delivery Charge': {
        defaultValue: delivery_cost,
        type: 'number',
        field: 'paid_delivery.delivery_cost',
        validation: yup.number().min(0).required()
      },
      'Healthera Service Charge': {
        defaultValue: delivery_fee,
        type: 'number',
        field: 'paid_delivery.delivery_fee',
        validation: yup.number().min(0).required()
      },
      'Concession Delivery Charge': {
        defaultValue: concession_cost,
        type: 'number',
        field: 'paid_delivery.concession_cost',
        validation: yup.number().min(0).required()
      },
      'Concession Service Charge': {
        defaultValue: concession_fee,
        type: 'number',
        field: 'paid_delivery.concession_fee',
        validation: yup.number().min(0).required()
      },
      'Transaction fee - fixed': {
        defaultValue: transaction,
        type: 'number',
        field: 'commissions.defaultRate.transaction_fee',
        validation: yup.number().min(0).max(REMEDY_PRICE).required()
      },
      'Transaction fee - %': {
        defaultValue: transactionPercent,
        type: 'number',
        field: 'commissions.defaultRate.transaction_fee_percentage',
        validation: yup.number().min(0).max(100).required()
      },
      'Referred Appointment fee': {
        defaultValue: referredAppointmentsFee,
        type: 'number',
        field: 'commissions.defaultRate.referred_appointments',
        validation: yup.number().min(0).optional(),
      },
      'Text Alert fee': {
        defaultValue: textAlertsFee,
        type: 'number',
        field: 'commissions.defaultRate.text_alerts',
        validation: yup.number().min(0).optional(),
      },
      'Local (Non-Referred) Consultation fee': {
        defaultValue: localConsultationFee,
        type: 'number',
        field: 'commissions.defaultRate.non_referred_consultation',
        validation: yup.number().min(0).nullable(true).optional(),
      },
      'Referred Consultation fee': {
        defaultValue: referredConsultationFee,
        type: 'number',
        field: 'commissions.defaultRate.referred_consultation',
        validation: yup.number().min(0).nullable(true).optional(),
      },
      'Service Commission Rate - %': {
        defaultValue: parseFloat((appointment_commission_rate_percentage * 100).toFixed(2)),
        type: 'number',
        field: 'commissions.defaultRate.appointment_commission_rate_percentage',
        validation: yup.number().min(0).max(100).optional(),
        placeholder: 'Enter as percentage (e.g., 6 for 6%)',
      },
      'Contract Start Date': {
        defaultValue: formatDateToString(startDate),
        field: 'currentContract.startDate',
        fieldName: 'startDate',
        type: 'date',
        validation: yup.string().required(),
      },
      'Contract Length (in months)': {
        defaultValue: count,
        field: 'currentContract.count',
        fieldName: 'count',
        placeholder: 'Months',
        validation: yup.string().required(),
      },
      'Contract Signed Date': {
        defaultValue: formatDateToString(contractSignedDate),
        type: 'date',
        field: 'currentContract.contractSignedDate',
        disabled: true,
      },
      'Contract End Date': {
        defaultValue: endDate && formatDateToString(endDate),
        field: 'currentContract.endDate',
        type: 'date',
        validation: yup.string().required(),
      },
      'Payment Start Date': {
        defaultValue: formatDateToString(paymentStartDate),
        type: 'date',
        field: 'currentContract.paymentStartDate',
        disabled: true,
      },
      'Trial Period (in months)': {
        disabled: true,
        placeholder: 'Months',
        defaultValue: trialPeriod,
      },
      'Notice Period': {
        disabled: true,
        defaultValue: '14 Days',
      },
      'Discount': {
        disabled: true,
        defaultValue: discount,
      },
      'Method': {
        disabled: true,
        defaultValue: 'Manual Invoice',
      }
    }
  }
}


function castEmptyStringToNull(value) {
  return value === '' ? null : value;
}
